import { QueryClient } from '@tanstack/react-query';
import {
  encodeSettingValue,
  isSettingName,
  SettingName
} from 'v5/api/system-settings/settingCatalog';
import {
  SystemSettingsApi,
  SystemSetting
} from 'v5/api/system-settings/system-settings.api';
import {
  AppScopeIdentity,
  appScopeUtils
} from 'v5/core/app-scope/appScope.types';
import {
  assertWriteScope,
  exactScopeOf,
  isSettingRowAt,
  resolveSettingValue
} from './resolveSystemSettings';
import {
  inheritFromParent,
  SystemSettingsMutationService
} from './SystemSettingsMutationService';
import { systemSettingsQueryKey } from './SystemSettingsService.impl';

/** Replace the matching name+scope row (or append) in the org settings cache. */
function replaceCachedSettingRow(
  queryClient: QueryClient,
  organizationId: string,
  row: SystemSetting
): SystemSetting[] {
  const key = systemSettingsQueryKey(organizationId);
  const previous = queryClient.getQueryData<SystemSetting[]>(key) ?? [];
  const next = [
    ...previous.filter(
      existing =>
        !isSettingRowAt(existing, row.name, {
          enterpriseWorkspaceId: row.enterprise_workspace_id ?? null,
          enterpriseTeamId: row.enterprise_team_id ?? null
        })
    ),
    row
  ];
  queryClient.setQueryData(key, next);
  return next;
}

export function createSystemSettingsMutationService(config: {
  api: SystemSettingsApi;
  queryClient: QueryClient;
}): SystemSettingsMutationService {
  const { api, queryClient } = config;

  async function updateSetting(
    scope: AppScopeIdentity,
    name: SettingName,
    value: unknown
  ) {
    assertWriteScope(scope, name);
    const row = await api.upsertSetting({
      organizationId: scope.organizationId,
      name,
      setting: encodeSettingValue(name, value),
      ...exactScopeOf(scope)
    });

    const rows = replaceCachedSettingRow(
      queryClient,
      scope.organizationId,
      row
    );
    return resolveSettingValue(rows, scope, name);
  }

  return {
    updateSetting,
    async updateSettings(
      scope: AppScopeIdentity,
      patch: Record<string, unknown>
    ) {
      const names = Object.keys(patch).filter(isSettingName);
      names.forEach(name => assertWriteScope(scope, name));
      const inherited = names.filter(name => patch[name] === inheritFromParent);
      const stored = names.filter(name => patch[name] !== inheritFromParent);
      if (inherited.length > 0 && scope.level === 'organization') {
        throw new Error(
          "The organisation's settings have no parent to inherit"
        );
      }
      const exact = exactScopeOf(scope);

      if (stored.length > 0) {
        const rows = await api.upsertSettings({
          organizationId: scope.organizationId,
          settings: Object.fromEntries(
            stored.map(name => [name, encodeSettingValue(name, patch[name])])
          ),
          ...exact
        });
        rows.forEach(row =>
          replaceCachedSettingRow(queryClient, scope.organizationId, row)
        );
      }
      if (inherited.length > 0) {
        await Promise.all(
          inherited.map(name =>
            api.deleteSetting({
              organizationId: scope.organizationId,
              name,
              enterpriseWorkspaceId: appScopeUtils.lowerTo('workspace', scope)
                .workspaceId,
              enterpriseTeamId: exact.enterpriseTeamId
            })
          )
        );
        const key = systemSettingsQueryKey(scope.organizationId);
        queryClient.setQueryData(
          key,
          (queryClient.getQueryData<SystemSetting[]>(key) ?? []).filter(
            row => !inherited.some(name => isSettingRowAt(row, name, exact))
          )
        );
      }
    }
  };
}
